import { Injectable } from '@angular/core';
import { HrDataProvider } from '../tokens';
import { OfflineStateService } from './offline-state.service';
import { OfflineSequenceService } from './offline-sequence.service';
import type {
  EmployeeFilterParams, OfflineEmployee, CreateEmployeeData,
  LeaveRequestFilterParams, OfflineLeaveRequest, CreateLeaveRequestData,
  PayRunFilterParams, OfflinePayRun, SuccessResult,
} from '../types/offline-models';

@Injectable({ providedIn: 'root' })
export class OfflineHrService implements HrDataProvider {
  constructor(
    private offlineState: OfflineStateService,
    private sequenceService: OfflineSequenceService,
  ) {}

  async getEmployees(params: EmployeeFilterParams): Promise<OfflineEmployee[]> {
    const db = this.offlineState.getDb();
    if (!db) throw new Error('Offline DB not initialized');
    const selector: Record<string, boolean> = {};
    if (params?.isActive !== undefined) selector['isActive'] = params.isActive;
    const docs = await db.employees.find({ selector }).exec();
    return docs.map(d => d.toJSON() as OfflineEmployee);
  }

  async createEmployee(data: CreateEmployeeData): Promise<SuccessResult> {
    const db = this.offlineState.getDb();
    if (!db) throw new Error('Offline DB not initialized');

    const now = new Date().toISOString();
    await db.employees.insert({
      id: String(Date.now()),
      ...data,
      isActive: true,
      updatedAt: now,
    });
    return { success: true };
  }

  async getLeaveRequests(params: LeaveRequestFilterParams): Promise<OfflineLeaveRequest[]> {
    const db = this.offlineState.getDb();
    if (!db) throw new Error('Offline DB not initialized');
    const selector: Record<string, string | number> = {};
    if (params?.employeeId) selector['employeeId'] = params.employeeId;
    if (params?.status) selector['status'] = params.status;
    const docs = await db.leave_requests.find({ selector }).exec();
    return docs.map(d => d.toJSON() as OfflineLeaveRequest);
  }

  async createLeaveRequest(data: CreateLeaveRequestData): Promise<SuccessResult> {
    const db = this.offlineState.getDb();
    if (!db) throw new Error('Offline DB not initialized');

    const requestNumber = await this.sequenceService.generateLeaveRequestNumber(data.userId);
    const now = new Date().toISOString();

    await db.leave_requests.insert({
      id: String(Date.now()),
      employeeId: data.employeeId,
      leaveTypeId: data.leaveTypeId,
      startDate: data.startDate,
      endDate: data.endDate,
      days: data.days,
      status: 'PENDING',
      comment: data.comment ?? null,
      rejectionReason: null,
      companyId: data.companyId,
      offlineId: requestNumber,
      syncStatus: 'PENDING',
      createdAt: now,
      updatedAt: now,
    });

    await this.offlineState.refreshPendingCount();
    return { success: true, requestNumber };
  }

  async getPayRuns(params: PayRunFilterParams): Promise<OfflinePayRun[]> {
    const db = this.offlineState.getDb();
    if (!db) throw new Error('Offline DB not initialized');
    const selector: Record<string, string> = {};
    if (params?.status) selector['status'] = params.status;
    const docs = await db.pay_runs.find({ selector }).exec();
    return docs.map(d => d.toJSON() as OfflinePayRun);
  }
}
